/**
 * The City scene — wiring only. Movement, collision and interaction rules
 * live in `src/logic/` as pure functions; this scene feeds them input and
 * draws their results. Logic runs on a fixed 60 Hz timestep with
 * interpolated rendering (106 §3), so behaviour is identical at any frame
 * rate. Order per tick: drain intents → movement/collision → interaction
 * zones → emit UI events. Render happens per frame, not per tick.
 */
import Phaser from 'phaser';
import { worldBus } from '@shared/bus.js';
import {
  BASE_HEIGHT,
  BASE_WIDTH,
  CAMERA_DAMPING,
  CAMERA_LOOKAHEAD,
  INDICATOR_FADE_IN_MS,
  INDICATOR_FADE_OUT_MS,
  SPRITE_H,
  TICK_MS,
  TILE,
} from '@shared/constants.js';
import { sessionState } from '@shared/session-state.js';
import { blockedKey, FACING_DELTA, tileAt, type TileGrid } from '@logic/grid.js';
import { activeNpc, type NpcPlacement } from '@logic/interaction.js';
import { boxCentre, stepPlayer, type PlayerState } from '@logic/movement.js';
import { IntentHub } from './input/intent-hub.js';
import { attachKeyboard } from './input/keyboard-source.js';
import { attachTouch } from './input/touch-source.js';
import { carAt, INVULNERABILITY_MS, type HazardDef } from '@game/time-model.ts';
import { FIRST_ROOM_NPCS, FIRST_ROOM_SPAWN, firstRoom } from './maps/first-room.js';
import {
  BAG_KEY,
  CAR_KEY,
  generatePlaceholderTextures,
  INDICATOR_KEY,
  LOCK_KEY,
  npcKey,
  playerKey,
  SPOT_KEY,
  tileKey,
  WARNING_KEY,
} from './textures.js';

export class CityScene extends Phaser.Scene {
  private grid!: TileGrid;
  private npcs: readonly NpcPlacement[] = [];
  private hub = new IntentHub();

  private state!: PlayerState;
  private prevState!: PlayerState;
  private accumulator = 0;

  private playerSprite!: Phaser.GameObjects.Image;
  private indicator!: Phaser.GameObjects.Image;
  private activeNpcId: string | null = null;
  private npcSprites = new Map<string, Phaser.GameObjects.Image>();
  private frozen = false;
  private locked = false;
  private tier: 0 | 1 | 2 | 3 = 0;
  private speedMultiplier = 1;
  private bagSprite: Phaser.GameObjects.Image | null = null;
  private hasMoved = false;

  // City systems (Block 12): hazards render deterministically from the wall
  // clock; locked areas add a blocker tile until the server says otherwise.
  private hazards: readonly HazardDef[] = [];
  private carSprites = new Map<string, Phaser.GameObjects.Image>();
  private warningSprites = new Map<string, Phaser.GameObjects.Image>();
  private lockSprites = new Map<string, Phaser.GameObjects.Image>();
  private localInvulnUntil = 0;

  // Fishing spots (US 20.02) + the boat movement mode (US 20.04).
  private fishingSpots: { id: string; x: number; y: number; deep: boolean }[] = [];
  private spotSprites = new Map<string, Phaser.GameObjects.Image>();
  private inputCaptured = false;
  private moveMode: 'foot' | 'boat' = 'foot';
  private hasBoat = false;

  constructor() {
    super('city');
  }

  create(): void {
    generatePlaceholderTextures(this);

    this.npcs = FIRST_ROOM_NPCS;
    // NPCs are solid: you talk to people, you don't walk through them.
    this.grid = {
      ...firstRoom(),
      blocked: new Set(this.npcs.map((n) => blockedKey(n.col, n.row))),
    };

    // Tiles. A static image per tile is plenty at 26×16; atlases arrive with
    // the real pipeline (US 17.08).
    for (let r = 0; r < this.grid.rows; r++) {
      for (let c = 0; c < this.grid.cols; c++) {
        const kind = this.grid.tiles[r]?.[c];
        if (kind)
          this.add
            .image(c * TILE, r * TILE, tileKey(kind))
            .setOrigin(0, 0)
            .setDepth(0);
      }
    }

    // NPCs: static, fixed facing. Sprite feet sit on the tile's bottom edge.
    for (const npc of this.npcs) {
      const sprite = this.add
        .image(npc.col * TILE + TILE / 2, (npc.row + 1) * TILE, npcKey(npc.facing))
        .setOrigin(0.5, 1)
        .setDepth((npc.row + 1) * TILE);
      this.npcSprites.set(npc.id, sprite);
    }

    // Tier 3 freezes world input (US 10.02); tiers 0–2 leave movement free.
    // Hazard damage suspends from tier 2 up: an offline client must not
    // rack up hits it cannot report or dodge fairly.
    worldBus.on('connection-tier', ({ tier }) => {
      this.tier = tier;
      this.frozen = this.locked || tier === 3;
    });
    // Session lock (US 15.05): a permanent freeze no tier change can undo.
    worldBus.on('session-locked', () => {
      this.locked = true;
      this.frozen = true;
    });

    // City info (US 03.01/03.07): hazards + area locks, server-fed. The
    // first sync may land before this scene exists — sessionState mirrors it.
    worldBus.on('city-info', (info) => this.applyCityInfo(info));
    // A modal minigame owns the input while open (US 20.01).
    worldBus.on('modal-input', ({ captured }) => {
      this.inputCaptured = captured;
    });
    if (sessionState.cityInfo) this.applyCityInfo(sessionState.cityInfo);

    // The corpse-run bag (US 01.07): rendered from server state, never local.
    worldBus.on('client-state', ({ deathDrop, hasBicycle, hasBoat }) => {
      // Bicycle: x2 movement, no slot cost (US 01.11, 108).
      this.speedMultiplier = hasBicycle ? 2 : 1;
      this.hasBoat = hasBoat;
      if (deathDrop) {
        if (!this.bagSprite) {
          this.bagSprite = this.add.image(0, 0, BAG_KEY).setOrigin(0.5, 1);
        }
        this.bagSprite.setPosition(deathDrop.x, deathDrop.y).setDepth(deathDrop.y).setVisible(true);
      } else {
        this.bagSprite?.setVisible(false);
      }
    });

    // Death respawn (US 01.07): back to the spawn point, full health —
    // the apartment respawn arrives with the apartment itself (Block 10).
    // Fast travel: the server already validated and charged (US 01.11).
    worldBus.on('travel-completed', ({ x, y }) => {
      this.state = { ...this.state, x, y, vx: 0, vy: 0 };
      this.prevState = this.state;
      this.hasMoved = true;
      this.cameras.main.centerOn(x, y - SPRITE_H / 2);
    });

    worldBus.on('player-died', () => {
      const x = FIRST_ROOM_SPAWN.col * TILE + TILE / 2;
      const y = (FIRST_ROOM_SPAWN.row + 1) * TILE;
      this.state = { ...this.state, x, y, vx: 0, vy: 0 };
      this.prevState = this.state;
      this.cameras.main.centerOn(x, y - SPRITE_H / 2);
    });

    // Paused-conversation marker (US 01.04): a distinct tint on the NPC.
    worldBus.on('npc-paused', ({ npcId, paused }) => {
      const sprite = this.npcSprites.get(npcId);
      if (sprite) {
        if (paused) sprite.setTint(0xffe082);
        else sprite.clearTint();
      }
    });

    // Player.
    const spawn: PlayerState = {
      x: FIRST_ROOM_SPAWN.col * TILE + TILE / 2,
      y: (FIRST_ROOM_SPAWN.row + 1) * TILE,
      vx: 0,
      vy: 0,
      facing: 'down',
    };
    this.state = spawn;
    this.prevState = spawn;
    this.playerSprite = this.add
      .image(spawn.x, spawn.y, playerKey('down'))
      .setOrigin(0.5, 1)
      .setDepth(spawn.y);

    // "!" indicator — hidden until an interaction target exists (US 01.02).
    this.indicator = this.add
      .image(0, 0, INDICATOR_KEY)
      .setOrigin(0.5, 1)
      .setAlpha(0)
      .setDepth(1e6);

    // Input sources — the only modules that know about devices (US 12.01).
    attachKeyboard(this.hub);
    attachTouch(this.hub, this.game.canvas);
    // The on-screen interact button (US 12.02) presses through the same funnel.
    worldBus.on('virtual-press', ({ intent }) => this.hub.press(intent));

    // Camera: hard bounds to the map, manual damped follow with lookahead.
    this.cameras.main.setBounds(0, 0, this.grid.cols * TILE, this.grid.rows * TILE);
    this.cameras.main.centerOn(spawn.x, spawn.y - SPRITE_H / 2);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.hub.detachAll());
  }

  override update(_time: number, delta: number): void {
    // Fixed timestep with an accumulator; render interpolates between the
    // last two logic states so movement is smooth at any refresh rate.
    this.accumulator += delta;
    // A background tab can bank seconds of delta; cap so we never spiral.
    this.accumulator = Math.min(this.accumulator, 10 * TICK_MS);
    while (this.accumulator >= TICK_MS) {
      this.tick(TICK_MS / 1000);
      this.accumulator -= TICK_MS;
    }

    const alpha = this.accumulator / TICK_MS;
    const rx = this.prevState.x + (this.state.x - this.prevState.x) * alpha;
    const ry = this.prevState.y + (this.state.y - this.prevState.y) * alpha;
    this.playerSprite.setPosition(Math.round(rx), Math.round(ry));
    this.playerSprite.setTexture(playerKey(this.state.facing));
    this.playerSprite.setDepth(ry);

    this.renderHazards();
    this.updateCamera(rx, ry);
  }

  private tick(dt: number): void {
    // Tier 3 disables ALL input (US 10.02); queued intents are not discarded
    // — the transport layer holds economy actions, and movement input simply
    // resumes on reconnect. Hazard damage suspends from tier 2 (see
    // checkHazardCollision).
    const frame =
      this.frozen || this.inputCaptured ? { move: { x: 0, y: 0 }, pressed: [] } : this.hub.drain();

    // Crash/tab-close recovery: adopt the saved position once, before the
    // player has issued any input of their own.
    if (sessionState.restorePosition && !this.hasMoved) {
      const p = sessionState.restorePosition;
      sessionState.restorePosition = null;
      this.state = { ...this.state, x: p.x, y: p.y };
      this.prevState = this.state;
      this.cameras.main.centerOn(p.x, p.y - SPRITE_H / 2);
    }
    if (frame.move.x !== 0 || frame.move.y !== 0) this.hasMoved = true;

    this.prevState = this.state;
    // Boat mode (US 20.04): water carries the boat at its own pace; the
    // bicycle multiplier applies only on foot.
    this.state = stepPlayer(
      this.state,
      frame.move,
      this.grid,
      dt,
      this.moveMode === 'boat' ? 1.6 : this.speedMultiplier,
      this.moveMode,
    );
    worldBus.emit('player-position', { x: this.state.x, y: this.state.y });

    this.checkHazardCollision();

    // Interaction zones — exactly one winner or none (US 01.02).
    const centre = boxCentre(this.state);
    const target = activeNpc(this.npcs, centre.x, centre.y);
    const targetId = target?.id ?? null;
    if (targetId !== this.activeNpcId) {
      this.activeNpcId = targetId;
      this.setIndicator(target);
      worldBus.emit('interaction-changed', { npcId: targetId });
    }

    // Discrete intents: INTERACT engages the active NPC; the rest are UI
    // traffic forwarded over the bus (the world never touches the DOM).
    for (const intent of frame.pressed) {
      if (intent === 'INTERACT') {
        // Priority: dropped bag > boat transition > fishing spot > NPC.
        const bag = this.bagSprite;
        const nearSpot = this.fishingSpots.find(
          (s) =>
            Math.hypot((s.x + 0.5) * TILE - this.state.x, (s.y + 0.5) * TILE - this.state.y) <=
            TILE * 2.2,
        );
        if (bag?.visible && Math.hypot(bag.x - this.state.x, bag.y - this.state.y) <= TILE * 1.5) {
          worldBus.emit('bag-collect-requested', {});
        } else if (this.tryBoatTransition()) {
          // handled — boarded or came ashore (US 20.04)
        } else if (nearSpot) {
          worldBus.emit('fishing-requested', { spotId: nearSpot.id });
        } else if (this.activeNpcId) {
          worldBus.emit('interact-requested', { npcId: this.activeNpcId });
        }
      } else if (intent !== 'CONFIRM') {
        worldBus.emit('ui-intent', { intent });
      }
    }
  }

  /**
   * Dock-to-dock boat transitions (US 20.04): on a dock facing water,
   * INTERACT boards (with the city's boat item); on water facing a dock,
   * INTERACT comes ashore. Returns true when a transition happened or was
   * meaningfully refused (so the press doesn't fall through to an NPC).
   */
  private tryBoatTransition(): boolean {
    const col = Math.floor(this.state.x / TILE);
    const row = Math.floor((this.state.y - 1) / TILE);
    const here = tileAt(this.grid, col, row);
    const look = FACING_DELTA[this.state.facing];
    const ahead = tileAt(this.grid, col + look.dx, row + look.dy);
    if (this.moveMode === 'foot' && here === 'dock' && ahead === 'water') {
      if (!this.hasBoat) {
        worldBus.emit('notice', {
          text: 'You need a boat to head out — someone in town rents them.',
          danger: false,
        });
        return true;
      }
      this.moveMode = 'boat';
      this.state = {
        ...this.state,
        x: (col + look.dx + 0.5) * TILE,
        y: (row + look.dy + 1) * TILE,
        vx: 0,
        vy: 0,
      };
      this.prevState = this.state;
      this.playerSprite.setTint(0x90caf9);
      return true;
    }
    if (this.moveMode === 'boat' && ahead === 'dock') {
      this.moveMode = 'foot';
      this.state = {
        ...this.state,
        x: (col + look.dx + 0.5) * TILE,
        y: (row + look.dy + 1) * TILE,
        vx: 0,
        vy: 0,
      };
      this.prevState = this.state;
      this.playerSprite.clearTint();
      return true;
    }
    return false;
  }

  private applyCityInfo(info: {
    hazards: HazardDef[];
    areas: { id: string; open: boolean; blockerTile?: { x: number; y: number } }[];
    fishingSpots?: { id: string; x: number; y: number; deep: boolean }[];
  }): void {
    this.hazards = info.hazards;
    // Fishing spots: the persistent visual affordance (US 20.02).
    this.fishingSpots = info.fishingSpots ?? [];
    for (const spot of this.fishingSpots) {
      if (!this.spotSprites.has(spot.id)) {
        this.spotSprites.set(
          spot.id,
          this.add
            .image((spot.x + 0.5) * TILE, (spot.y + 1) * TILE, SPOT_KEY)
            .setOrigin(0.5, 1)
            .setDepth((spot.y + 1) * TILE - 1),
        );
      }
    }
    // Closed areas put a lock on their blocker tile and make it solid;
    // unlocking (server-said) removes both. Never inferred client-side.
    const blocked = new Set(this.grid.blocked ?? []);
    for (const area of info.areas) {
      const tile = area.blockerTile;
      if (!tile) continue;
      const key = blockedKey(tile.x, tile.y);
      const existing = this.lockSprites.get(area.id);
      if (!area.open && !existing) {
        blocked.add(key);
        this.lockSprites.set(
          area.id,
          this.add
            .image(tile.x * TILE, tile.y * TILE, LOCK_KEY)
            .setOrigin(0, 0)
            .setDepth((tile.y + 1) * TILE + 1),
        );
      } else if (area.open && existing) {
        blocked.delete(key);
        existing.destroy();
        this.lockSprites.delete(area.id);
      }
    }
    this.grid = { ...this.grid, blocked };
  }

  /** Cars are pure functions of the wall clock (US 03.07) — no scene state. */
  private renderHazards(): void {
    const now = Date.now();
    for (const hazard of this.hazards) {
      let car = this.carSprites.get(hazard.id);
      if (!car) {
        car = this.add.image(0, 0, CAR_KEY).setOrigin(0.5, 1).setVisible(false);
        this.carSprites.set(hazard.id, car);
      }
      const pos = carAt(hazard, now);
      if (pos) {
        const px = (pos.x + 0.5) * TILE;
        const py = (pos.y + 1) * TILE;
        car.setPosition(Math.round(px), Math.round(py)).setDepth(py).setVisible(true);
      } else {
        car.setVisible(false);
      }

      // The tell (US 03.07): ≥1s warning at the spawn point before each car.
      let warn = this.warningSprites.get(hazard.id);
      if (!warn) {
        const origin = hazard.path[0] ?? { x: 0, y: 0 };
        warn = this.add
          .image((origin.x + 0.5) * TILE, origin.y * TILE, WARNING_KEY)
          .setOrigin(0.5, 0.5)
          .setDepth(1e6 - 1)
          .setVisible(false);
        this.warningSprites.set(hazard.id, warn);
      }
      const toNextMs = hazard.spawnIntervalMs - (now % hazard.spawnIntervalMs);
      const warning = !pos && toNextMs <= 1000;
      warn.setVisible(warning);
      if (warning) warn.setAlpha(0.5 + 0.5 * Math.abs(Math.sin(now / 120)));
    }
  }

  /** Client-side hit detection; the SERVER decides whether it counts. */
  private checkHazardCollision(): void {
    // Tier ≥2 suspends damage (US 10.02) — see the connection-tier note.
    if (this.tier >= 2) return;
    const now = Date.now();
    if (now < this.localInvulnUntil) return;
    const centre = boxCentre(this.state);
    for (const hazard of this.hazards) {
      const pos = carAt(hazard, now);
      if (!pos) continue;
      const carX = (pos.x + 0.5) * TILE;
      const carY = (pos.y + 0.5) * TILE;
      if (Math.hypot(carX - centre.x, carY - centre.y) <= TILE * 0.7) {
        // Local invuln mirrors the server's 800ms window; the flash is
        // presentation — the authoritative check runs server-side.
        this.localInvulnUntil = now + INVULNERABILITY_MS;
        this.tweens.add({
          targets: this.playerSprite,
          alpha: 0.25,
          duration: 100,
          yoyo: true,
          repeat: 3,
          onComplete: () => this.playerSprite.setAlpha(1),
        });
        worldBus.emit('hazard-collision', { hazardId: hazard.id });
        return;
      }
    }
  }

  private setIndicator(target: NpcPlacement | null): void {
    this.tweens.killTweensOf(this.indicator);
    if (target) {
      // Above the NPC's head, correctly positioned (US 01.02).
      this.indicator.setPosition(
        target.col * TILE + TILE / 2,
        (target.row + 1) * TILE - SPRITE_H - 6,
      );
      this.tweens.add({
        targets: this.indicator,
        alpha: 1,
        duration: INDICATOR_FADE_IN_MS,
      });
    } else {
      this.tweens.add({
        targets: this.indicator,
        alpha: 0,
        duration: INDICATOR_FADE_OUT_MS,
      });
    }
  }

  /** Damped follow with 1-tile lookahead; never shows outside map bounds. */
  private updateCamera(px: number, py: number): void {
    const cam = this.cameras.main;
    const look = FACING_DELTA[this.state.facing];
    const moving = this.state.vx !== 0 || this.state.vy !== 0;
    const targetX = px + (moving ? look.dx * CAMERA_LOOKAHEAD : 0) - BASE_WIDTH / 2;
    const targetY = py - SPRITE_H / 2 + (moving ? look.dy * CAMERA_LOOKAHEAD : 0) - BASE_HEIGHT / 2;

    const maxX = this.grid.cols * TILE - BASE_WIDTH;
    const maxY = this.grid.rows * TILE - BASE_HEIGHT;
    const clampedX = Phaser.Math.Clamp(targetX, 0, Math.max(0, maxX));
    const clampedY = Phaser.Math.Clamp(targetY, 0, Math.max(0, maxY));

    cam.setScroll(
      cam.scrollX + (clampedX - cam.scrollX) * CAMERA_DAMPING,
      cam.scrollY + (clampedY - cam.scrollY) * CAMERA_DAMPING,
    );
  }
}
